import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combine class names + dedupe Tailwind conflicts.
 * Standard utility used by every component.
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Format a number as a localized integer with thousands separators.
 * 1234 -> "1,234"
 */
export function formatNumber(n) {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US').format(n);
}

/**
 * Format a USD price. 450 cents -> "$4.50"
 */
export function formatUSD(cents) {
  if (cents == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

/**
 * Format a number compactly. 1234 -> "1.2K", 1500000 -> "1.5M"
 */
export function formatCompact(n) {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(n);
}

/**
 * Format an ISO date as a short relative string ("2 hours ago")
 * or fallback to a short date for older timestamps.
 */
export function formatRelative(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  const now = Date.now();
  const seconds = Math.floor((now - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Format an ISO date as a short absolute date ("Jun 25, 2026").
 */
export function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Copy text to clipboard. Returns true on success.
 */
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Mask an API key for safe display.
 * sk_aBcDeFgHiJ... -> sk_aBcD********
 */
export function maskKey(prefix, length = 4) {
  return `${prefix}${'*'.repeat(Math.max(length, 8))}`;
}